import type { GiftPassEmailType } from '../../api/gift-pass-actions';
import type { GiftPassDetail } from '../../api/gift-passes';

export const VOID_REASON_MAX_LENGTH = 500;
export const COMP_REASON_MAX_LENGTH = 500;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function canVoidPass(pass: Pick<GiftPassDetail, 'status'>): boolean {
  return pass.status === 'ISSUED' || pass.status === 'SENT';
}

export interface ResendOption {
  type: GiftPassEmailType;
  label: string;
  emailName: string;
  toEmail: string;
  /** More about what the email holds, when it covers more than this pass. */
  note?: string;
}

/** The recipient of the send that went out most recently and was not cancelled. */
function deliveredRecipient(pass: GiftPassDetail): string | null {
  let latest: GiftPassDetail['sends'][number] | null = null;
  for (const send of pass.sends) {
    if (send.sentAt === null || send.cancelledAt !== null) continue;
    if (!latest || send.sentAt > (latest.sentAt ?? '')) latest = send;
  }
  return latest?.recipientEmail ?? null;
}

export function resendOptions(pass: GiftPassDetail): ResendOption[] {
  if (pass.status === 'VOIDED' || pass.status === 'EXPIRED') return [];
  const options: ResendOption[] = [];
  const to = pass.purchaser.email;
  if (pass.source === 'PURCHASE' && pass.order) {
    options.push({
      type: 'GIFT_PASS_PURCHASE_CONFIRMATION',
      label: 'Re-send purchase confirmation',
      emailName: 'the purchase confirmation',
      toEmail: to,
      note: 'It lists every pass on the order that has not been voided or expired.',
    });
  }
  if (pass.source === 'REFUND') {
    options.push({
      type: 'GIFT_PASS_REFUND',
      label: 'Re-send refund email',
      emailName: 'the refund email',
      toEmail: to,
      note: 'It lists every pass from that refund that has not been voided or expired.',
    });
  }
  // A one-pass email for a used pass would hand out a dead code as a gift.
  const unused = pass.status !== 'REDEEMED';
  if (pass.source === 'COMP' && unused) {
    options.push({
      type: 'GIFT_PASS_COMP',
      label: 'Re-send comp email',
      emailName: 'the comp email',
      toEmail: to,
    });
  }
  const recipient = unused ? deliveredRecipient(pass) : null;
  if (recipient) {
    options.push({
      type: 'GIFT_PASS_RECIPIENT',
      label: 'Re-send to recipient',
      emailName: "the recipient's gift email",
      toEmail: recipient,
    });
  }
  return options;
}

export function voidReasonError(reason: string): string | null {
  const trimmed = reason.trim();
  if (trimmed.length === 0) return 'Enter a reason.';
  if (trimmed.length > VOID_REASON_MAX_LENGTH) {
    return `Keep the reason to ${VOID_REASON_MAX_LENGTH} characters or fewer.`;
  }
  return null;
}

export interface CompFormValues {
  purchaserEmail: string;
  reason: string;
}

export type CompFormErrors = Partial<Record<keyof CompFormValues, string>>;

export function validateCompForm(values: CompFormValues): CompFormErrors {
  const errors: CompFormErrors = {};
  if (!EMAIL.test(values.purchaserEmail.trim())) {
    errors.purchaserEmail = 'Enter a valid email address.';
  }
  const reason = values.reason.trim();
  if (reason.length === 0) errors.reason = 'Enter a reason.';
  else if (reason.length > COMP_REASON_MAX_LENGTH) {
    errors.reason = `Keep the reason to ${COMP_REASON_MAX_LENGTH} characters or fewer.`;
  }
  return errors;
}

export function actionErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export interface AvailableGiftPassActions {
  canVoid: boolean;
  resends: ResendOption[];
}

export function availableGiftPassActions(
  pass: GiftPassDetail,
  holds: { void: boolean; resend: boolean },
): AvailableGiftPassActions {
  return {
    canVoid: holds.void && canVoidPass(pass),
    resends: holds.resend ? resendOptions(pass) : [],
  };
}

export function hasAnyGiftPassAction(actions: AvailableGiftPassActions): boolean {
  return actions.canVoid || actions.resends.length > 0;
}
